import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Calendar, DollarSign, MessageSquare, Phone, Mail, Send, Clock } from 'lucide-react';

const PaymentReminderCard = ({ 
  isExpanded, 
  onToggle, 
  advancePaid, 
  requiredAdvance,
  selectedCustomer,
  customers,
  orderDate,
  onReminderChange,
  initialData = null
}) => {
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [customReason, setCustomReason] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('12:00');
  const [amountToCollect, setAmountToCollect] = useState(0);
  const [channels, setChannels] = useState([]);
  const [notes, setNotes] = useState('');
  const [autoSnooze, setAutoSnooze] = useState('+1d');
  const initialized = useRef(false);

  const reasonOptions = [
    'Advance below 80%',
    'No cash carried',
    'Post-dated cheque',
    'Custom'
  ];

  const channelOptions = [
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
    { value: 'sms', label: 'SMS', icon: Phone },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'call', label: 'Call', icon: Phone },
    { value: 'push', label: 'App Push', icon: Bell }
  ];

  // Initialize from initialData ONCE when provided (for edit mode)
  // Use a ref to prevent re-initialization on every state change
  useEffect(() => {
    if (initialData && initialData.enabled && !initialized.current) {
      console.log('Initializing PaymentReminderCard with data:', initialData);
      setReminderEnabled(true);
      setReasons(initialData.reason_tags || []);
      setDueDate(initialData.due_at ? initialData.due_at.split('T')[0] : '');
      setDueTime(initialData.due_at ? initialData.due_at.split('T')[1]?.substring(0, 5) || '12:00' : '12:00');
      setAmountToCollect(initialData.amount_due || 0);
      setChannels(initialData.channels || []);
      setNotes(initialData.notes || '');
      setAutoSnooze(initialData.auto_snooze || '+1d');
      initialized.current = true;
    }
  }, [initialData]);

  // Auto-prefill when expanded (DO NOT auto-enable)
  useEffect(() => {
    if (isExpanded && advancePaid < requiredAdvance) {
      // Set default due date (tomorrow 12:00 PM)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDueDate(tomorrow.toISOString().split('T')[0]);
      setDueTime('12:00');
      
      // Set amount to collect (rounded)
      const deficit = requiredAdvance - advancePaid;
      setAmountToCollect(Math.round(deficit));
      
      // Set default reason
      setReasons(['Advance below 80%']);
      
      // Set default channels
      setChannels(['whatsapp', 'push']);
    }
  }, [isExpanded, advancePaid, requiredAdvance]);

  // Notify parent of changes
  useEffect(() => {
    if (reminderEnabled) {
      // Build final reasons array with custom reason if selected
      const finalReasons = reasons.includes('Custom') && customReason.trim()
        ? [...reasons.filter(r => r !== 'Custom'), customReason]
        : reasons;
        
      const reminderData = {
        enabled: reminderEnabled,
        reasons: finalReasons,
        due_at: `${dueDate}T${dueTime}:00`,
        amount_due: Math.round(parseFloat(amountToCollect)),
        contact_id: selectedCustomer,
        channels,
        notes,
        auto_snooze: autoSnooze
      };
      onReminderChange?.(reminderData);
    } else {
      // Send enabled: false to trigger backend deletion
      onReminderChange?.({ enabled: false });
    }
  }, [reminderEnabled, reasons, customReason, dueDate, dueTime, amountToCollect, selectedCustomer, channels, notes, autoSnooze]);

  const toggleReason = (reason) => {
    setReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const toggleChannel = (channel) => {
    setChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  const setQuickDate = (days) => {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + days);
    setDueDate(targetDate.toISOString().split('T')[0]);
  };

  const getStatusBadge = () => {
    if (!reminderEnabled) {
      return <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">Off</span>;
    }
    if (dueDate && dueTime) {
      const dueDateTime = new Date(`${dueDate}T${dueTime}`);
      const formatted = dueDateTime.toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'short', 
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-semibold">Set for {formatted}</span>;
    }
    return null;
  };

  return (
    <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-amber-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-amber-600" />
          <span className="font-bold text-gray-800">Payment Reminder</span>
          {getStatusBadge()}
        </div>
        <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-amber-200">
          {/* Toggle Switch */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-gray-700">Set Payment Reminder</label>
            <button
              type="button"
              onClick={() => setReminderEnabled(!reminderEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                reminderEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  reminderEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {reminderEnabled && (
            <>
              {/* Reminder Reason */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Reminder Reason</label>
                <div className="flex flex-wrap gap-2">
                  {reasonOptions.map(reason => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => toggleReason(reason)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        reasons.includes(reason)
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                
                {/* Custom Reason Input */}
                {reasons.includes('Custom') && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Enter custom reason..."
                      className="input-field w-full"
                    />
                  </div>
                )}
              </div>

              {/* Due Date & Time */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Due Date & Time</label>
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setQuickDate(1)} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200">+1d</button>
                  <button type="button" onClick={() => setQuickDate(3)} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200">+3d</button>
                  <button type="button" onClick={() => setQuickDate(7)} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200">+7d</button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="input-field flex-1"
                    required
                  />
                  <input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              {/* Amount to Collect */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Amount to Collect (₹)</label>
                <input
                  type="number"
                  value={amountToCollect}
                  onChange={(e) => setAmountToCollect(Math.round(parseFloat(e.target.value) || 0))}
                  className="input-field w-full"
                  placeholder="Enter amount"
                  required
                  step="1"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Amount will be rounded to nearest rupee</p>
              </div>

              {/* Channels */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Channel(s)</label>
                <div className="flex flex-wrap gap-2">
                  {channelOptions.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleChannel(value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        channels.includes(value)
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input-field w-full"
                  rows="3"
                  placeholder="Add any additional notes..."
                />
              </div>

              {/* Auto-Snooze */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Auto-Snooze</label>
                <select
                  value={autoSnooze}
                  onChange={(e) => setAutoSnooze(e.target.value)}
                  className="input-field w-full"
                >
                  <option value="+1d">+1 day (Default)</option>
                  <option value="+3d">+3 days</option>
                  <option value="+1w">+1 week</option>
                  <option value="None">None</option>
                </select>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-700">
                  💡 <strong>Auto-resolve:</strong> Reminder will automatically resolve when advance is paid or order is delivered.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentReminderCard;
